import { Box, Card, Group, Stack, Text, TextInput, Tooltip, UnstyledButton } from '@mantine/core';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';

type PaletteColourFieldProps = {
  label: string;
  description?: string;
  value: string;
  onChange: (value: string) => void;
  onFocus: () => void;
  onBlur: () => void;
  swatchColours?: string[];
  onPickSwatch?: (hex: string) => void;
};

export default function PaletteColourField({
  label,
  description,
  value,
  onChange,
  onFocus,
  onBlur,
  swatchColours = [],
  onPickSwatch,
}: PaletteColourFieldProps) {
  return (
    <Card withBorder radius="md" p="md">
      <Stack gap="sm">
        <Group justify="space-between" align="center" wrap="nowrap">
          <Group gap="md" align="center" wrap="nowrap">
            <Box
              style={{
                position: 'relative',
                width: 44,
                height: 44,
                flexShrink: 0,
                borderRadius: '50%',
                overflow: 'hidden',
                border: '1px solid var(--mantine-color-gray-3)',
              }}
            >
              <input
                type="color"
                aria-label={`${label} colour picker`}
                value={value}
                onChange={event => onChange(event.currentTarget.value)}
                onFocus={onFocus}
                onBlur={onBlur}
                style={{
                  position: 'absolute',
                  top: -8,
                  left: -8,
                  width: 'calc(100% + 16px)',
                  height: 'calc(100% + 16px)',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                }}
              />
            </Box>
            <Stack gap={2}>
              <Text size="textSm" fw={FONT_WEIGHTS.semibold} c="gray.7">
                {label}
              </Text>
              {description && (
                <Text size="xs" c="gray.6" lh={1.2}>
                  {description}
                </Text>
              )}
            </Stack>
          </Group>
          <TextInput
            aria-label={`${label} hex colour`}
            value={value}
            onChange={event => onChange(event.currentTarget.value)}
            onFocus={onFocus}
            onBlur={onBlur}
            w={110}
          />
        </Group>
        {swatchColours.length > 0 && onPickSwatch && (
          <Group gap={6} wrap="wrap">
            {swatchColours.map((hex, index) => {
              const isSelected = hex.toLowerCase() === value.toLowerCase();
              return (
                <Tooltip key={`${hex}-${index}`} label={hex}>
                  <UnstyledButton
                    aria-label={`Use ${hex} for ${label.toLowerCase()}`}
                    onClick={() => onPickSwatch(hex)}
                  >
                    <Box
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        backgroundColor: hex,
                        border: isSelected
                          ? '2px solid var(--mantine-color-blue-6)'
                          : '1px solid var(--mantine-color-gray-3)',
                      }}
                    />
                  </UnstyledButton>
                </Tooltip>
              );
            })}
          </Group>
        )}
      </Stack>
    </Card>
  );
}
